import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Alert,
} from '@mui/material';
import type { CreateUserPayload } from '../../../services/adminUsers';

const ROLE_OPTIONS = ['employee', 'manager', 'finance', 'admin', 'super_admin'] as const;
const SCOPE_OPTIONS = ['none', 'team', 'all'] as const;

export interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (payload: CreateUserPayload) => Promise<void> | void;
  isSubmitting?: boolean;
  error?: string | null;
}

export const CreateUserDialog: React.FC<CreateUserDialogProps> = ({ open, onClose, onCreate, isSubmitting, error }) => {
  const [email, setEmail] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [role, setRole] = useState<string>('employee');
  const [department, setDepartment] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isApprover, setIsApprover] = useState<boolean>(false);
  const [approvalScope, setApprovalScope] = useState<string>('none');
  const [approvalLimit, setApprovalLimit] = useState<string>('');
  const [localError, setLocalError] = useState<string | null>(null);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setEmail('');
      setName('');
      setRole('employee');
      setDepartment('');
      setIsActive(true);
      setIsApprover(false);
      setApprovalScope('none');
      setApprovalLimit('');
      setLocalError(null);
    }
  }, [open]);

  const scopeDisabled = useMemo(() => !isApprover, [isApprover]);
  const limitDisabled = useMemo(() => !isApprover || approvalScope === 'none', [isApprover, approvalScope]);

  const handleCreate = async () => {
    setLocalError(null);
    // Basic validation
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setLocalError('Please enter a valid email');
      return;
    }
    if (!name.trim()) {
      setLocalError('Name is required');
      return;
    }
    if (!limitDisabled) {
      const num = Number(approvalLimit);
      if (!num || num <= 0) {
        setLocalError('Approval limit must be greater than 0 when scope is not none');
        return;
      }
    }

    const payload: CreateUserPayload = {
      email: email.trim(),
      name: name.trim(),
      role: role as any,
      department: department.trim() || null,
      is_active: isActive,
      is_approver: isApprover,
      approval_scope: approvalScope as any,
      approval_limit: limitDisabled ? null : Number(approvalLimit),
      auth_type: 'db_email_otp',
    };

    await onCreate(payload);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      container={() => document.getElementById('root') as HTMLElement}
    >
      <DialogTitle>Add User</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {localError && (
            <Grid item xs={12}>
              <Alert severity="error">{localError}</Alert>
            </Grid>
          )}
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Full Name"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Role"
              select
              fullWidth
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              {ROLE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Department"
              fullWidth
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
              label="Active"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={isApprover} onChange={(e) => setIsApprover(e.target.checked)} />}
              label="Is Approver"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Approval Scope"
              select
              fullWidth
              value={approvalScope}
              onChange={(e) => setApprovalScope(e.target.value)}
              disabled={scopeDisabled}
            >
              {SCOPE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Approval Limit"
              type="number"
              inputProps={{ min: 0, step: 1 }}
              fullWidth
              value={approvalLimit}
              onChange={(e) => setApprovalLimit(e.target.value)}
              disabled={limitDisabled}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={!!isSubmitting}>Cancel</Button>
        <Button variant="contained" onClick={handleCreate} disabled={!!isSubmitting}>Create</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUserDialog;
